import React from "react";
import {
  Box,
  ButtonWithAction,
  Checkbox,
  Cluster,
  Paragraph,
  Stack,
  Title,
  util,
} from "@thecb/components";
import { REFUND_TYPES } from "@/sanctuary/typedefs";
import {
  TableBody,
  Table,
  TableFooterRow,
  TableHeaderRow,
} from "@/components/generic/Table";
import { PAYMENT_MODIFICATION_TYPES } from "../../Payment.state";
import { amountToCents, resolveLineItemDescriptions } from "@/util/common";
import { RefundHeaderText, RefundTotalText } from "./PartialRefundContainer.styled";
import FullLineItem from "./full-line-item/FullLineItem";
import PartialLineItem from "./partial-refund-line-item/PartialLineItem";

const refundIncludesItem = (refund, item) => {
  return refund.lineItems.some((refundItem) => {
    return (
      refundItem.id.value === item.id.value &&
      refundItem.amountRefundedInCents.value > 0
    );
  });
};

const PartialRefundContainer = ({
  payment,
  partialRefundItems,
  closePartialRefundContainer,
  toggleRefundAllLineItems,
  setPartialRefundLineItem,
  submitVoidRefundModal,
  refundRequestPending,
  goToPayment,
  getDescriptionKeys,
  getSubdescriptionKeys,
}) => {
  const obligationSlug = payment?.obligationSlug.value;
  const descriptionKeys = getDescriptionKeys(obligationSlug);
  const subdescriptionKeys = getSubdescriptionKeys(obligationSlug);
  const refunds = payment.associatedPayments.filter(
    (assoc) => assoc.paymentProcessTypeName.value === "refund"
  );

  const setIsRefunded = ({ lineItem, isRefunded }) => {
    setPartialRefundLineItem({
      id: lineItem.id.value,
      amount: isRefunded ? lineItem.amountRefundableInCents.value / 100 : null,
    });
  };

  const updatePartialLineItem = ({ lineItem, amount }) => {
    setPartialRefundLineItem({ id: lineItem.id.value, amount });
  };

  const totalRefundAmountCents =
    partialRefundItems.map(({ amount }) => amount).reduce((sum, v) => sum + v, 0) *
    100;

  const amountsAreValid = partialRefundItems.every(({ id, amount }) => {
    const item = payment.lineItems.find((item) => item.id.value === id);
    const safeRefundableAmount = item?.amountRefundableInCents?.value || 0;
    const safeEnteredAmount = Number(amount) || 0;

    const maxAmountInCents = BigInt(safeRefundableAmount);
    const enteredAmountInCents = amountToCents(safeEnteredAmount);

    return maxAmountInCents >= enteredAmountInCents && enteredAmountInCents > 0;
  });

  const allItemsRefunded = payment.lineItems.every((item) =>
    partialRefundItems.find(({ id }) => id === item.id.value)
  );

  return (
    <Box padding="1.5rem">
      <Title
        as="h6"
        weight="600"
        margin="0 0 0.5rem 0"
        extraStyles="font-size: 1.25rem;"
      >
        Refund Amount
      </Title>

      <Paragraph variant="pS" margin="0 0 1.5rem 0">
        {
          "Please choose which line items and the amount for each line item you'd like to refund."
        }
      </Paragraph>

      <Box
        padding="0"
        width="auto"
        boxShadow="0px 0px 5px rgba(202, 206, 216, 1)"
        borderRadius="4px"
        extraStyles="margin-bottom: 1.5rem;"
      >
        <Table>
          <TableBody>
            <TableHeaderRow margin={"16px"} padding={"16px"} autoHeight>
              <th>
                <Cluster align="center">
                  <Checkbox
                    checked={allItemsRefunded}
                    onChange={() => {
                      toggleRefundAllLineItems({
                        isSet: !allItemsRefunded,
                        lineItems: payment.lineItems,
                      });
                    }}
                  />
                  <RefundHeaderText>Description</RefundHeaderText>
                </Cluster>
              </th>
              <th>
                <RefundHeaderText>Refund status</RefundHeaderText>
              </th>
            </TableHeaderRow>

            {payment.lineItems.map((item) => {
              const refund = partialRefundItems.find(
                ({ id }) => id === item.id.value
              );

              const { description, subDescription } = resolveLineItemDescriptions(
                item.customAttributes,
                descriptionKeys,
                subdescriptionKeys
              );

              const commonProps = {
                key: item.id.value,
                lineItem: item,
                description,
                subDescription,
                previousRefunds: refunds.filter((refund) =>
                  refundIncludesItem(refund, item)
                ),
                isRefunded: Boolean(refund),
                setIsRefunded: setIsRefunded,
                goToPayment: goToPayment,
              };

              return payment.refundTypeAllowed?.value?.value ===
                REFUND_TYPES.PARTIAL_AMOUNT_FULL_LINE_ITEM ? (
                <FullLineItem {...commonProps} />
              ) : (
                <PartialLineItem
                  refundAmount={refund?.amount}
                  setRefundAmount={updatePartialLineItem}
                  {...commonProps}
                />
              );
            })}

            <TableFooterRow margin={"16px"} padding={"16px"} autoHeight>
              <td>
                <RefundTotalText>Refund Total</RefundTotalText>
              </td>
              <td>
                <RefundTotalText>
                  {util.general.displayCurrency(totalRefundAmountCents)}
                </RefundTotalText>
              </td>
            </TableFooterRow>
          </TableBody>
        </Table>
      </Box>

      <Stack justify="flex-end" direction="row">
        <ButtonWithAction
          variant="back"
          text="Cancel"
          borderRadius="4px"
          extraStyles="margin: 0;"
          action={() => closePartialRefundContainer()}
        />

        <ButtonWithAction
          className="primary-button"
          variant="primary"
          text="Refund"
          borderRadius="4px"
          extraStyles="margin: 0;"
          disabled={!amountsAreValid || totalRefundAmountCents <= 0}
          isLoading={refundRequestPending}
          action={() => {
            if (!refundRequestPending) {
              submitVoidRefundModal(PAYMENT_MODIFICATION_TYPES.REFUND);
            }
          }}
        />
      </Stack>
    </Box>
  );
};

export default PartialRefundContainer;
